import { printHTML, downloadWord } from '../utils/printHelper';
import { useEffect, useState, useMemo } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDatePrint } from '../utils/dateFormat';
import { DateInput } from '../components/DateInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash, MagnifyingGlass, Baby, Check, X, Printer } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';
import ClientAutocomplete from '../components/ClientAutocomplete';

export default function PorodiljskoPage() {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterVrsta, setFilterVrsta] = useState('all');
  const [zahtjevOpen, setZahtjevOpen] = useState(false);
  const [zahtjevEntry, setZahtjevEntry] = useState(null);
  const [zahtjevMjesec, setZahtjevMjesec] = useState('');

  const emptyForm = {
    client_id: '', naziv_klijenta: '', porodilja: '', pocetak: '',
    zavrsetak: '', iznos_za_refundaciju: 0, vrsta: 'porodiljsko',
    mjeseci: [], napomena: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [clientRadnici, setClientRadnici] = useState([]);
  const [radnikSearch, setRadnikSearch] = useState('');
  const [radnikResults, setRadnikResults] = useState([]);
  const [showRadnikDropdown, setShowRadnikDropdown] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [p, c] = await Promise.all([api.get('/porodiljsko'), api.get('/clients')]);
      setEntries(p.data);
      setClients(c.data.sort((a, b) => (a.skraceni_naziv || '').localeCompare(b.skraceni_naziv || '')));
    } catch { toast.error('Greška'); }
    finally { setLoading(false); }
  };

  const fetchClientRadnici = async (clientId) => {
    if (!clientId) { setClientRadnici([]); return; }
    try {
      const r = await api.get(`/prijava-radnika?client_id=${clientId}&status=aktivan`);
      setClientRadnici(r.data);
    } catch { setClientRadnici([]); }
  };

  const searchRadnik = (query) => {
    setRadnikSearch(query);
    setForm(prev => ({ ...prev, porodilja: query }));
    if (query.length < 1) { setRadnikResults([]); setShowRadnikDropdown(false); return; }
    // Search from active workers of selected client
    if (form.client_id && clientRadnici.length > 0) {
      const q = query.toLowerCase();
      const matches = clientRadnici.filter(r => {
        const name = `${r.prezime || ''} ${r.ime || ''}`.toLowerCase();
        return name.includes(q) || (r.jmb || '').includes(q);
      });
      setRadnikResults(matches.slice(0, 15));
      setShowRadnikDropdown(matches.length > 0);
      return;
    }
    // If no radnici, search fizicka lica
    if (query.length >= 2) {
      api.get(`/fizicka-lica?search=${encodeURIComponent(query)}`).then(r => {
        setRadnikResults(r.data.slice(0, 10).map(fl => ({ ...fl, prezime: fl.prezime, ime: fl.ime })));
        setShowRadnikDropdown(r.data.length > 0);
      }).catch(() => {});
    }
  };

  const selectRadnik = (r) => {
    const name = `${(r.prezime || '').toUpperCase()} ${(r.ime || '').toUpperCase()}`.trim();
    setForm(prev => ({ ...prev, porodilja: name }));
    setRadnikSearch(name);
    setShowRadnikDropdown(false);
  };

  const handleClientSelect = (cid) => {
    if (cid === 'none') return;
    const c = clients.find(cl => cl.id === cid);
    if (c) {
      setForm(prev => ({ ...prev, client_id: cid, naziv_klijenta: c.skraceni_naziv || c.naziv_firme || '' }));
      fetchClientRadnici(cid);
    }
  };

  // Load radnici when form opens with a client
  useEffect(() => {
    if (isFormOpen && form.client_id) fetchClientRadnici(form.client_id);
  }, [isFormOpen, form.client_id]);

  const handleSubmit = async () => {
    if (!form.naziv_klijenta || !form.porodilja) { toast.error('Unesite naziv klijenta i ime porodilje'); return; }
    try {
      if (editingId) { await api.put(`/porodiljsko/${editingId}`, form); toast.success('Ažurirano'); }
      else { await api.post('/porodiljsko', form); toast.success('Kreirano'); }
      setIsFormOpen(false); setEditingId(null); setForm(emptyForm); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Greška'); }
  };

  const handleEdit = (z) => {
    setEditingId(z.id);
    setForm({
      client_id: z.client_id || '', naziv_klijenta: z.naziv_klijenta || '',
      porodilja: z.porodilja || '', pocetak: z.pocetak || '',
      zavrsetak: z.zavrsetak || '', iznos_za_refundaciju: z.iznos_za_refundaciju || 0,
      vrsta: z.vrsta || 'porodiljsko', mjeseci: z.mjeseci || [], napomena: z.napomena || '',
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog('Obrisati?'))) return;
    try { await api.delete(`/porodiljsko/${id}`); toast.success('Obrisano'); fetchData(); } catch { toast.error('Greška'); }
  };

  const toggleMjesec = (m) => {
    setForm(prev => ({
      ...prev,
      mjeseci: prev.mjeseci.includes(m) ? prev.mjeseci.filter(x => x !== m) : [...prev.mjeseci, m]
    }));
  };

  const filtered = useMemo(() => {
    let list = entries;
    if (filterVrsta !== 'all') list = list.filter(e => e.vrsta === filterVrsta);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(e =>
        (e.naziv_klijenta || '').toLowerCase().includes(s) ||
        (e.porodilja || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [entries, filterVrsta, search]);

  const fmtIznos = (v) => {
    if (!v) return '-';
    return parseFloat(v).toFixed(2).replace('.', ',');
  };

  const MJESEC_NAMES = ['', 'Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun', 'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'];

  // Count working days (Mon-Fri) between two dates
  const countRadniDani = (startDate, endDate) => {
    let count = 0;
    const d = new Date(startDate);
    const end = new Date(endDate);
    while (d <= end) {
      const day = d.getDay();
      if (day !== 0 && day !== 6) count++;
      d.setDate(d.getDate() + 1);
    }
    return count;
  };

  // Get total working days in a month
  const radniDaniUMjesecu = (year, month) => {
    const first = new Date(year, month - 1, 1);
    const last = new Date(year, month, 0);
    return countRadniDani(first, last);
  };

  // Calculate actual amount for a specific month
  const calculateMjesecniIznos = (z, targetYear, targetMonth) => {
    const puniIznos = parseFloat(z.iznos_za_refundaciju) || 0;
    if (!z.pocetak) return puniIznos;

    const pocParts = z.pocetak.split('-');
    const pocYear = parseInt(pocParts[0]);
    const pocMonth = parseInt(pocParts[1]);
    const pocDay = parseInt(pocParts[2]);

    let zavYear, zavMonth, zavDay;
    if (z.zavrsetak) {
      const zavParts = z.zavrsetak.split('-');
      zavYear = parseInt(zavParts[0]);
      zavMonth = parseInt(zavParts[1]);
      zavDay = parseInt(zavParts[2]);
    }

    const totalRD = radniDaniUMjesecu(targetYear, targetMonth);
    if (totalRD === 0) return puniIznos;

    const isFirstMonth = (targetYear === pocYear && targetMonth === pocMonth);
    const isLastMonth = z.zavrsetak && (targetYear === zavYear && targetMonth === zavMonth);

    if (isFirstMonth && isLastMonth) {
      // Start and end in same month
      const startD = new Date(pocYear, pocMonth - 1, pocDay);
      const endD = new Date(zavYear, zavMonth - 1, zavDay);
      const rd = countRadniDani(startD, endD);
      return Math.round((puniIznos / totalRD) * rd * 100) / 100;
    } else if (isFirstMonth) {
      // First month - from start date to end of month
      const startD = new Date(pocYear, pocMonth - 1, pocDay);
      const lastDay = new Date(targetYear, targetMonth, 0);
      const rd = countRadniDani(startD, lastDay);
      return Math.round((puniIznos / totalRD) * rd * 100) / 100;
    } else if (isLastMonth) {
      // Last month - from 1st to end date
      const firstDay = new Date(zavYear, zavMonth - 1, 1);
      const endD = new Date(zavYear, zavMonth - 1, zavDay);
      const rd = countRadniDani(firstDay, endD);
      return Math.round((puniIznos / totalRD) * rd * 100) / 100;
    } else {
      // Full month
      return puniIznos;
    }
  };

  // Get target month/year from entry start date + month number
  const getTargetMonthYear = (z, mjesecNum) => {
    if (!z.pocetak) return { mjesecName: '', godina: '', targetMonth: 0, targetYear: 0 };
    const parts = z.pocetak.split('-');
    let startMonth = parseInt(parts[1]);
    let startYear = parseInt(parts[0]);
    let tMonth = startMonth + parseInt(mjesecNum) - 1;
    let tYear = startYear;
    while (tMonth > 12) { tMonth -= 12; tYear++; }
    while (tMonth < 1) { tMonth += 12; tYear--; }
    return { mjesecName: MJESEC_NAMES[tMonth] || '', godina: tYear.toString(), targetMonth: tMonth, targetYear: tYear };
  };

  const buildZahtjevHTML = (z, mjesecNum) => {
    const client = clients.find(c => c.id === z.client_id) || {};
    const firmaPuniNaziv = client.naziv_firme || z.naziv_klijenta || '_______________';
    const firmaAdresa = client.adresa || '';
    const firmaGrad = client.grad || 'Ulcinj';
    const firmaTelefon = client.telefon || '';
    const firmaEmail = client.email || '';
    const firmaPib = client.pib || '_______________';
    const ziroRacun = client.pt_ziro_racun || client.ziro_racun_1 || '_______________';
    const direktor = client.direktor || '_______________';
    const vrstaText = z.vrsta === 'porodiljsko' ? 'porodiljskog' : 'roditeljskog';
    const radnikText = z.vrsta === 'porodiljsko' ? 'radnicu' : 'radnika';

    const { mjesecName, godina, targetMonth, targetYear } = getTargetMonthYear(z, mjesecNum);
    if (!mjesecName) return '';

    const mjesecniIznos = calculateMjesecniIznos(z, targetYear, targetMonth);
    const iznosStr = mjesecniIznos.toFixed(2).replace('.', ',');

    // Calculate period dates
    const pocParts = z.pocetak ? z.pocetak.split('-') : [];
    const pocYear = parseInt(pocParts[0]) || targetYear;
    const pocMonth = parseInt(pocParts[1]) || 1;
    const pocDay = parseInt(pocParts[2]) || 1;
    const zavParts = z.zavrsetak ? z.zavrsetak.split('-') : [];
    const zavYear = parseInt(zavParts[0]) || 0;
    const zavMonth = parseInt(zavParts[1]) || 0;
    const zavDay = parseInt(zavParts[2]) || 0;
    const isFirstMonth = (targetYear === pocYear && targetMonth === pocMonth);
    const isLastMonth = z.zavrsetak && (targetYear === zavYear && targetMonth === zavMonth);
    let pStart = isFirstMonth ? pocDay : 1;
    let pEnd = isLastMonth ? zavDay : new Date(targetYear, targetMonth, 0).getDate();
    const periodStr = `${pStart.toString().padStart(2,'0')}.${targetMonth.toString().padStart(2,'0')}.-${pEnd.toString().padStart(2,'0')}.${targetMonth.toString().padStart(2,'0')}.${targetYear}`;

    const datum = new Date();
    const datumStr = `${datum.getDate().toString().padStart(2,'0')}.${(datum.getMonth()+1).toString().padStart(2,'0')}.${datum.getFullYear()}`;
    const brojStr = `${parseInt(mjesecNum)}/${godina}`;
    const telEmail = [firmaTelefon ? `Tel: ${firmaTelefon}` : '', firmaEmail ? `E-mail: ${firmaEmail}` : ''].filter(Boolean).join('  ');

    return `<div class="doc">
  <div class="sender">
    <p>${firmaPuniNaziv.toUpperCase()}</p>
    <p>${firmaGrad.toUpperCase()}, ${firmaAdresa.toUpperCase()}</p>
    <p>PIB:${firmaPib}, ZIRO RACUN: ${ziroRacun}</p>
    ${telEmail ? `<p>${telEmail}</p>` : ''}
  </div>

  <div class="br-date">
    <p>Br.${brojStr}</p>
    <p>${firmaGrad}, ${datumStr}.god</p>
  </div>

  <div class="recipient">
    CENTAR ZA SOCIJALNI RAD BAR I ULCINJ<br>
    ULCINJ
  </div>

  <div class="predmet">PREDMET: ZAHTJEV ZA REFUNDACIJU</div>

  <div class="body">
    Molimo da izvršite refundaciju naknade zarade po osnovu ${vrstaText}
    odsustva za mjesec ${periodStr}. godine za našu
    ${radnikText} ${z.porodilja} u iznosu od: ${iznosStr} EUR
  </div>
  <div class="body">
    Uplatu izvršite na žiro-račun br. ${ziroRacun}.
  </div>
  <div class="body">
    U prilogu zahtjeva dostavljamo potrebnu dokumentaciju.
  </div>

  <div class="sign-block">
    <div class="spacer"></div>
    <div class="sign-mp">M.P.</div>
    <div class="spacer"></div>
    <div class="sign-right">
      Direktor,<br>
      <strong>${direktor}</strong>
      <span class="line"></span>
    </div>
  </div>
</div>`;
  };

  const printCSS = `
@page{size:A4;margin:10mm 25mm}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;font-size:12pt;color:#000;line-height:1.5}
.doc{max-width:170mm;margin:0 auto;page-break-after:always;padding-top:10mm}
.doc:last-child{page-break-after:auto}
p{margin:0}
.sender{font-size:12pt;line-height:1.4;margin-bottom:20px;text-align:center;font-weight:bold}
.br-date{font-size:12pt;margin-bottom:35px;line-height:1.4}
.recipient{text-align:center;font-size:12pt;font-weight:bold;margin-bottom:25px;line-height:1.5}
.predmet{font-weight:bold;font-size:12pt;margin-bottom:20px;margin-top:60px}
.body{font-size:12pt;line-height:1.7;margin-bottom:10px}
.sign-block{display:flex;align-items:flex-start;margin-top:60px;font-size:12pt;line-height:1.5}
.sign-block .spacer{flex:1}
.sign-mp{flex:0 0 auto;text-align:center;font-weight:bold;padding-top:18px;min-width:90px}
.sign-right{flex:0 0 auto;text-align:center;min-width:240px}
.sign-right .line{border-top:1px solid #000;display:block;width:220px;margin:30px auto 0 auto}
`;

  const buildZahtjevPageHtml = (z, mjesecNum) => {
    const html = buildZahtjevHTML(z, mjesecNum);
    if (!html) return '';
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Zahtjev za refundaciju</title>
<style>${printCSS}</style></head><body>${html}
<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script>
</body></html>`;
  };

  const handlePrintZahtjev = (z, mjesecNum) => {
    const full = buildZahtjevPageHtml(z, mjesecNum);
    if (!full) { toast.error('Nema podataka za ovaj mjesec'); return; }
    printHTML(full);
  };

  const handleWordZahtjev = (z, mjesecNum) => {
    const full = buildZahtjevPageHtml(z, mjesecNum);
    if (!full) { toast.error('Nema podataka za ovaj mjesec'); return; }
    downloadWord(full, `Zahtjev_refundacija_${(z.ime_prezime||'radnik').replace(/\s+/g,'_')}_mj${mjesecNum}.doc`);
  };

  // Generate all zahtjevi for a specific month number - SKIP already checked ones
  const handlePrintAllZahtjevi = (mjesecNum) => {
    // Only entries that do NOT have this month checked (checked = already done)
    const mStr = mjesecNum.toString();
    const needsZahtjev = entries.filter(e => {
      // Must have a valid start date and be active for this month number
      if (!e.pocetak) return false;
      const totalMonths = e.mjeseci?.length || 0;
      // Must NOT already be checked for this month
      if ((e.mjeseci || []).includes(mStr)) return false;
      // Must be within the range of months for this entry
      const maxMonth = 13; // max months
      return parseInt(mStr) <= maxMonth;
    });

    if (needsZahtjev.length === 0) { toast.error('Svi zahtjevi za ovaj mjesec su već genrisani (čekirani)'); return; }

    const pages = needsZahtjev.map(z => buildZahtjevHTML(z, mStr)).filter(Boolean);
    if (pages.length === 0) { toast.error('Nema zahtjeva za generisanje'); return; }

    printHTML(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Zahtjevi za refundaciju - Mjesec ${mjesecNum}</title>
<style>${printCSS}</style></head><body>
${pages.join('\n')}
<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script>
</body></html>`);
    toast.success(`Generisano ${pages.length} zahtjeva za mjesec ${mjesecNum}`);
  };

  const handleOpenZahtjev = (z) => {
    setZahtjevEntry(z);
    setZahtjevMjesec('');
    setZahtjevOpen(true);
  };

  const handleGenerateZahtjev = () => {
    if (!zahtjevEntry || !zahtjevMjesec) { toast.error('Izaberite mjesec'); return; }
    handlePrintZahtjev(zahtjevEntry, zahtjevMjesec);
    setZahtjevOpen(false);
  };

  const handlePrintPrazan = (z) => {
    const client = clients.find(c => c.id === z.client_id) || {};
    const firmaPuniNaziv = client.naziv_firme || z.naziv_klijenta || '________________________';
    const firmaAdresa = client.adresa || '________________________';
    const firmaGrad = client.grad || '____________';
    const firmaTelefon = client.telefon || '';
    const firmaEmail = client.email || '';
    const firmaPib = client.pib || '____________';
    const ziroRacun = client.pt_ziro_racun || client.ziro_racun_1 || '________________________';
    const direktor = client.direktor || '________________________';
    const telEmail = [firmaTelefon ? `Tel: ${firmaTelefon}` : '', firmaEmail ? `E-mail: ${firmaEmail}` : ''].filter(Boolean).join('  ');

    printHTML(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Prazan zahtjev za refundaciju</title>
<style>${printCSS}</style></head><body>
<div class="doc">
  <div class="sender">
    <p>${firmaPuniNaziv.toUpperCase()}</p>
    <p>${firmaGrad.toUpperCase()}, ${firmaAdresa.toUpperCase()}</p>
    <p>PIB:${firmaPib}, ZIRO RACUN: ${ziroRacun}</p>
    ${telEmail ? `<p>${telEmail}</p>` : ''}
  </div>

  <div class="br-date">
    <p>Br.____/______</p>
    <p>${firmaGrad}, __.__.______.god</p>
  </div>

  <div class="recipient">
    CENTAR ZA SOCIJALNI RAD BAR I ULCINJ<br>
    ULCINJ
  </div>

  <div class="predmet">PREDMET: ZAHTJEV ZA REFUNDACIJU</div>

  <div class="body">
    Molimo da izvršite refundaciju naknade zarade po osnovu ___________________
    odsustva za mjesec __.__.-__.__.______. godine za našu
    radnicu/radnika ________________________________ u iznosu od: __________ EUR
  </div>
  <div class="body">
    Uplatu izvršite na žiro-račun br. ${ziroRacun}.
  </div>
  <div class="body">
    U prilogu zahtjeva dostavljamo potrebnu dokumentaciju.
  </div>

  <div class="sign-block">
    M.P.<br><br>
    Direktor,<br>
    ${direktor}<br>
    <span class="line"></span>
  </div>
</div>
<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script>
</body></html>`);
  };

  // Number of months in an entry: calculated from pocetak→zavrsetak
  const getMonthCount = (entry) => {
    if (!entry?.pocetak) return 14;
    const sp = entry.pocetak.split('-');
    if (sp.length !== 3) return 14;
    const sy = parseInt(sp[0]);
    const sm = parseInt(sp[1]);
    if (!entry.zavrsetak) return 14;
    const ep = entry.zavrsetak.split('-');
    if (ep.length !== 3) return 14;
    const ey = parseInt(ep[0]);
    const em = parseInt(ep[1]);
    const n = (ey - sy) * 12 + (em - sm) + 1;
    if (n < 1) return 1;
    if (n > 36) return 36; // safety cap
    return n;
  };

  // Generate month labels: MM/YY — receives optional entry for dynamic range
  const getMjesecLabels = (entry) => {
    const count = entry ? getMonthCount(entry) : 14;
    const labels = [];
    for (let i = 1; i <= count; i++) labels.push(i.toString());
    return labels;
  };

  // Max months across all entries — for consistent table header
  const getTableMaxMonths = () => {
    let max = 14;
    for (const e of filtered) {
      const n = getMonthCount(e);
      if (n > max) max = n;
    }
    return max;
  };

  const getTableMjesecLabels = () => {
    const count = getTableMaxMonths();
    const labels = [];
    for (let i = 1; i <= count; i++) labels.push(i.toString());
    return labels;
  };

  // Get M/YY label for a specific entry and month number
  const getMjesecDisplay = (entry, mjesecNum) => {
    if (!entry || !entry.pocetak) return mjesecNum;
    const parts = entry.pocetak.split('-');
    let startMonth = parseInt(parts[1]);
    let startYear = parseInt(parts[0]);
    let tMonth = startMonth + parseInt(mjesecNum) - 1;
    let tYear = startYear;
    while (tMonth > 12) { tMonth -= 12; tYear++; }
    while (tMonth < 1) { tMonth += 12; tYear--; }
    return `${tMonth}/${(tYear % 100).toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6" data-testid="porodiljsko-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Porodiljsko i roditeljsko odsustvo</h3>
          <p className="text-sm text-slate-600 mt-1">Evidencija i praćenje refundacija</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setEditingId(null); setIsFormOpen(true); }}
          className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="porodiljsko-add-btn">
          <Plus size={18} className="mr-2" /> Novi unos
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-3 text-center"><p className="text-2xl font-bold text-slate-800">{entries.length}</p><p className="text-xs text-slate-500">Ukupno</p></Card>
        <Card className="p-3 text-center bg-pink-50 border-pink-200"><p className="text-2xl font-bold text-pink-700">{entries.filter(e => e.vrsta === 'porodiljsko').length}</p><p className="text-xs text-pink-600">Porodiljsko</p></Card>
        <Card className="p-3 text-center bg-purple-50 border-purple-200"><p className="text-2xl font-bold text-purple-700">{entries.filter(e => e.vrsta === 'roditeljsko').length}</p><p className="text-xs text-purple-600">Roditeljsko</p></Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pretraži po firmi ili imenu..."
              className="pl-9 bg-white border-slate-300 text-sm" data-testid="porodiljsko-search" />
          </div>
          <Select value={filterVrsta} onValueChange={setFilterVrsta}>
            <SelectTrigger className="bg-white border-slate-300 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sve vrste</SelectItem>
              <SelectItem value="porodiljsko">Porodiljsko</SelectItem>
              <SelectItem value="roditeljsko">Roditeljsko</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-slate-500 self-center">{filtered.length} od {entries.length}</p>
        </div>
      </Card>

      {/* Table */}
      {loading ? <p className="text-center text-slate-500 py-16">Učitavanje...</p> : filtered.length === 0 ? (
        <Card className="p-8 text-center">
          <Baby size={48} weight="duotone" className="text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">{entries.length === 0 ? 'Nema unosa' : 'Nema rezultata za filter'}</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="porodiljsko-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-3 py-3 text-xs font-semibold uppercase text-slate-500 whitespace-nowrap">Naziv klijenta</th>
                  <th className="px-3 py-3 text-xs font-semibold uppercase text-slate-500 whitespace-nowrap">Porodilja</th>
                  <th className="px-3 py-3 text-xs font-semibold uppercase text-slate-500 whitespace-nowrap">Vrsta</th>
                  <th className="px-3 py-3 text-xs font-semibold uppercase text-slate-500 whitespace-nowrap">Početak</th>
                  <th className="px-3 py-3 text-xs font-semibold uppercase text-slate-500 whitespace-nowrap">Završetak</th>
                  <th className="px-3 py-3 text-xs font-semibold uppercase text-slate-500 whitespace-nowrap">Iznos</th>
                  {getTableMjesecLabels().map(m => (
                    <th key={m} className="px-1 py-3 text-[9px] font-semibold text-slate-400 text-center w-10">{m}</th>
                  ))}
                  <th className="px-3 py-3 text-xs font-semibold uppercase text-slate-500">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(z => (
                  <tr key={z.id} className={`border-b hover:bg-slate-50 ${z.vrsta === 'roditeljsko' ? 'bg-green-50/30' : ''}`}>
                    <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap text-xs">{z.naziv_klijenta || '-'}</td>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap text-xs">{z.porodilja || '-'}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${z.vrsta === 'porodiljsko' ? 'bg-pink-100 text-pink-700 border-pink-300' : 'bg-purple-100 text-purple-700 border-purple-300'}`}>
                        {z.vrsta === 'porodiljsko' ? 'Porod.' : 'Rodit.'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{formatDatePrint(z.pocetak) || '-'}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{formatDatePrint(z.zavrsetak) || '-'}</td>
                    <td className="px-3 py-2 text-xs font-mono text-slate-700 whitespace-nowrap">{fmtIznos(z.iznos_za_refundaciju)}</td>
                    {getTableMjesecLabels().map(m => {
                      const checked = (z.mjeseci || []).includes(m);
                      const inRange = parseInt(m) <= getMonthCount(z);
                      const mDisplay = getMjesecDisplay(z, m);
                      return (
                        <td key={m} className="px-1 py-2 text-center">
                          {!inRange ? (
                            <span className="text-slate-200 text-[10px]">—</span>
                          ) : checked ? (
                            <button onClick={() => handlePrintZahtjev(z, m)} className="w-full hover:bg-green-100 rounded px-0.5" title={`Štampaj zahtjev za ${mDisplay}`}>
                              <span className="text-green-700 font-bold text-[10px]">{mDisplay}</span>
                            </button>
                          ) : <span className="text-slate-300 text-[10px]">-</span>}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2">
                      <div className="flex gap-0.5">
                        <button onClick={() => handleEdit(z)} className="p-1.5 hover:bg-blue-100 rounded" title="Uredi"><Pencil size={14} className="text-blue-600" /></button>
                        <button onClick={() => handleDelete(z.id)} className="p-1.5 hover:bg-red-100 rounded" title="Obriši"><Trash size={14} className="text-red-600" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-lg" data-testid="porodiljsko-form">
          <DialogHeader><DialogTitle>{editingId ? 'Uredi unos' : 'Novi unos'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Izaberi klijenta</Label>
              <ClientAutocomplete clients={clients} value={form.client_id} onChange={(id, c) => handleClientSelect(id || 'none', c)} testid="porodiljsko-client-select" placeholder="Pretraga po nazivu ili PIB-u..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Naziv klijenta *</Label>
                <Input value={form.naziv_klijenta} onChange={e => setForm({...form, naziv_klijenta: e.target.value})} className="bg-white border-slate-300 text-sm" />
              </div>
              <div className="space-y-1 relative">
                <Label className="text-xs text-slate-500">Ime porodilje / radnika *</Label>
                <Input value={radnikSearch || form.porodilja}
                  onChange={e => searchRadnik(e.target.value)}
                  onFocus={() => { if (radnikResults.length > 0) setShowRadnikDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowRadnikDropdown(false), 200)}
                  placeholder="Kucajte ime ili prezime..." className="bg-white border-slate-300 text-sm" data-testid="porodiljsko-ime" autoComplete="off" />
                {showRadnikDropdown && radnikResults.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {radnikResults.map(r => (
                      <button key={r.id} type="button" onMouseDown={e => { e.preventDefault(); selectRadnik(r); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-0">
                        <span className="font-medium">{r.prezime} {r.ime}</span>
                        <span className="text-slate-400 text-xs ml-2">{r.naziv_radnog_mjesta || ''}</span>
                      </button>
                    ))}
                  </div>
                )}
                {form.client_id && clientRadnici.length === 0 && (
                  <p className="text-xs text-amber-500 mt-1">Nema aktivnih radnika za ovog klijenta</p>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Vrsta odsustva</Label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({...form, vrsta: 'porodiljsko'})}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border-2 ${form.vrsta === 'porodiljsko' ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-pink-600 border-pink-300'}`}>
                  Porodiljsko
                </button>
                <button type="button" onClick={() => setForm({...form, vrsta: 'roditeljsko'})}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border-2 ${form.vrsta === 'roditeljsko' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-purple-600 border-purple-300'}`}>
                  Roditeljsko
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Početak</Label>
                <DateInput value={form.pocetak} onChange={e => setForm({...form, pocetak: e.target.value})} className="border-slate-300 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Završetak</Label>
                <DateInput value={form.zavrsetak} onChange={e => setForm({...form, zavrsetak: e.target.value})} className="border-slate-300 text-sm" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Iznos za refundaciju (EUR)</Label>
              <Input type="number" step="0.01" value={form.iznos_za_refundaciju} onChange={e => setForm({...form, iznos_za_refundaciju: parseFloat(e.target.value) || 0})} className="bg-white border-slate-300 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Mjeseci - <span className="text-green-600">zeleno = urađeno</span> (klik na zeleno = štampaj zahtjev)</Label>
              <div className="flex flex-wrap gap-1">
                {getMjesecLabels(form).map(m => {
                  const isChecked = form.mjeseci.includes(m);
                  const mLabel = form.pocetak ? getMjesecDisplay(form, m) : m;
                  return (
                    <button key={m} type="button" 
                      onClick={() => {
                        if (isChecked && editingId) {
                          const entry = entries.find(e => e.id === editingId);
                          if (entry) handlePrintZahtjev({...entry, ...form}, m);
                        } else {
                          toggleMjesec(m);
                        }
                      }}
                      className={`min-w-[40px] px-1 h-8 rounded text-[10px] font-bold border-2 transition-colors ${
                        isChecked 
                          ? 'bg-green-600 text-white border-green-700 hover:bg-green-700 cursor-pointer' 
                          : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-100'
                      }`}
                      title={isChecked ? `Štampaj zahtjev za ${mLabel}` : `Označi ${mLabel}`}>
                      {mLabel}
                    </button>
                  );
                })}
              </div>
              {editingId && <p className="text-xs text-slate-400 mt-1">Kliknite zeleni mjesec da štampate zahtjev</p>}
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Napomena</Label>
              <textarea value={form.napomena} onChange={e => setForm({...form, napomena: e.target.value})}
                className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[50px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex gap-3 justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => setIsFormOpen(false)}>Odustani</Button>
              <Button onClick={handleSubmit} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="porodiljsko-submit">
                {editingId ? 'Ažuriraj' : 'Kreiraj'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
